import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { rfqApi, evaluationApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";
import type { QuotationSupplierEntry, RFQ } from "../../types";

type SupplierRow = Omit<QuotationSupplierEntry, "totalScore">;

const emptyRow = (): SupplierRow => ({
  supplierId: null,
  supplierName: "",
  amountQuoted: 0,
  deliveryPeriod: "",
  remarks: "",
  qualityScore: 0,
  deliveryScore: 0,
  pricingScore: 0,
  responsivenessScore: 0,
  complianceScore: 0,
});

function calcTotal(row: SupplierRow): number {
  return (row.qualityScore ?? 0) + (row.deliveryScore ?? 0) + (row.pricingScore ?? 0) + (row.responsivenessScore ?? 0) + (row.complianceScore ?? 0);
}

/** Turn an RFQ's invited-supplier list into blank scoring rows, ready for the officer to fill
 *  in amountQuoted/deliveryPeriod/remarks/scores from the physical quotations received. */
function rowsFromRfq(rfq: RFQ): SupplierRow[] {
  const invited = rfq.suppliers ?? [];
  if (!invited.length) return [emptyRow()];
  return invited.map((s) => ({
    ...emptyRow(),
    supplierId: s.supplierId ?? null,
    supplierName: s.supplierName ?? s.adHocName ?? "",
  }));
}

export default function EvaluationCreatePage() {
  const navigate = useNavigate();

  const { data: pendingRfqs, loading: rfqsLoading } = useAsyncData(
    () => rfqApi.pendingEvaluation().then((r) => r.content),
    [],
  );

  const [rfqId, setRfqId] = useState<number | "">("");
  const [rfqLoading, setRfqLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([emptyRow()]);
  const [recommendedSupplierId, setRecommendedSupplierId] = useState<number | "">("");
  const [recommendedSupplierName, setRecommendedSupplierName] = useState("");
  const [recommendationReason, setRecommendationReason] = useState("");

  async function handleRfqChange(value: string) {
    const id = value ? Number(value) : "";
    setRfqId(id);
    if (id === "") return;
    setRfqLoading(true);
    try {
      const rfq = await rfqApi.get(id);
      setSuppliers(rowsFromRfq(rfq));
      if (!description.trim()) {
        const itemSummary = rfq.lines.map((l) => l.description).filter(Boolean).join(", ");
        setDescription(`Evaluation of quotations for RFQ ${rfq.serialNo}${itemSummary ? ` — ${itemSummary}` : ""}`);
      }
    } finally {
      setRfqLoading(false);
    }
  }

  // Keep the "Recommended Supplier" dropdown in sync with whichever supplier rows currently exist.
  useEffect(() => {
    if (recommendedSupplierId !== "" && !suppliers.some((s) => s.supplierId === recommendedSupplierId)) {
      setRecommendedSupplierId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers]);

  const { mutate, loading, error } = useMutation(async () => {
    if (!description.trim()) throw new Error("Description is required.");
    const recommended = suppliers.find((s) => (recommendedSupplierId !== "" ? s.supplierId === recommendedSupplierId : false));
    const result = await evaluationApi.create({
      rfqId: rfqId !== "" ? (rfqId as number) : null,
      description,
      suppliers: suppliers.map((s) => ({ ...s, totalScore: calcTotal(s) })),
      recommendedSupplierId: recommendedSupplierId !== "" ? (recommendedSupplierId as number) : null,
      recommendedSupplierName: recommended?.supplierName || recommendedSupplierName || null,
      recommendationReason: recommendationReason || null,
    });
    navigate(`/procurement/evaluations/${result.id}`);
  });

  function addSupplier() {
    setSuppliers((prev) => [...prev, emptyRow()]);
  }

  function removeSupplier(idx: number) {
    setSuppliers((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateSupplier(idx: number, field: keyof SupplierRow, value: string | number) {
    setSuppliers((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  const scores = suppliers.map(calcTotal);
  const maxScore = Math.max(...scores, 0);
  // Named suppliers (registered, prefilled from the RFQ) are eligible for the recommendation dropdown.
  const namedSuppliers = suppliers.filter((s) => s.supplierId != null && s.supplierName);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/evaluations" className="text-sm text-emerald-700 hover:underline">&larr; Back to Evaluations</Link>
      </div>

      <ProcForm formNo="PROC-FRM-03" title="Quotation Evaluation">
        {rfqsLoading && <ProcLoading label="Loading pending RFQs..." />}
        {error && <ProcError message={error} />}

        <ProcSection title="Evaluation Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Linked RFQ</label>
              <select
                value={rfqId}
                onChange={(e) => handleRfqChange(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 text-sm"
              >
                <option value="">— select an RFQ pending evaluation —</option>
                {(pendingRfqs ?? []).map((r) => (
                  <option key={r.id} value={r.id}>{r.serialNo} {r.requisitionSerialNo ? `(${r.requisitionSerialNo})` : ""}</option>
                ))}
              </select>
              {rfqLoading && <p className="mt-1 text-xs text-slate-400">Loading RFQ details…</p>}
              <p className="mt-1 text-xs text-slate-400">Only RFQs already sent and not yet evaluated are listed. Selecting one pre-fills the suppliers below.</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-xl border px-3 py-2 text-sm"
                required
              />
            </div>
          </div>
        </ProcSection>

        <ProcSection title="Scoring Guide">
          <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800 space-y-1">
            <p className="font-bold mb-2">Evaluation Criteria & Weights</p>
            <div className="grid sm:grid-cols-2 gap-1 text-xs">
              <span>Quality: <strong>30%</strong> (0–30 pts)</span>
              <span>Delivery: <strong>25%</strong> (0–25 pts)</span>
              <span>Pricing: <strong>20%</strong> (0–20 pts)</span>
              <span>Responsiveness: <strong>15%</strong> (0–15 pts)</span>
              <span>Compliance: <strong>10%</strong> (0–10 pts)</span>
            </div>
            <p className="mt-2 font-semibold text-xs">Score Bands: 90–100 Excellent | 80–89 Good | 70–79 Fair | &lt;70 Unsatisfactory</p>
          </div>
        </ProcSection>

        <ProcSection title="Supplier Entries">
          <p className="mb-3 text-xs text-slate-500">
            {rfqId !== "" ? "Rows below were prefilled from the RFQ's invited suppliers — fill in amount quoted, delivery period, and scores." : "Select a Linked RFQ above to prefill suppliers, or add rows manually."}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left">Supplier</th>
                  <th className="px-2 py-2 text-right">Quoted (KES)</th>
                  <th className="px-2 py-2 text-left">Delivery</th>
                  <th className="px-2 py-2 text-right w-20">Quality<br />/30</th>
                  <th className="px-2 py-2 text-right w-20">Delivery<br />/25</th>
                  <th className="px-2 py-2 text-right w-20">Pricing<br />/20</th>
                  <th className="px-2 py-2 text-right w-20">Response<br />/15</th>
                  <th className="px-2 py-2 text-right w-20">Comply<br />/10</th>
                  <th className="px-2 py-2 text-right w-20">Total<br />/100</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {suppliers.map((s, idx) => {
                  const total = calcTotal(s);
                  const isTop = total > 0 && total === maxScore && suppliers.length > 1;
                  const isPrefilled = s.supplierId != null;
                  return (
                    <tr key={idx} className={isTop ? "bg-emerald-50" : ""}>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={s.supplierName}
                          onChange={(e) => updateSupplier(idx, "supplierName", e.target.value)}
                          placeholder="Name"
                          readOnly={isPrefilled}
                          className={`w-full rounded border px-2 py-1 text-xs ${isPrefilled ? "bg-slate-50 text-slate-600" : ""}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={s.amountQuoted} min={0} onChange={(e) => updateSupplier(idx, "amountQuoted", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="text" value={s.deliveryPeriod} onChange={(e) => updateSupplier(idx, "deliveryPeriod", e.target.value)} placeholder="e.g. 7 days" className="w-full rounded border px-2 py-1 text-xs" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={s.qualityScore ?? 0} min={0} max={30} onChange={(e) => updateSupplier(idx, "qualityScore", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={s.deliveryScore ?? 0} min={0} max={25} onChange={(e) => updateSupplier(idx, "deliveryScore", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={s.pricingScore ?? 0} min={0} max={20} onChange={(e) => updateSupplier(idx, "pricingScore", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={s.responsivenessScore ?? 0} min={0} max={15} onChange={(e) => updateSupplier(idx, "responsivenessScore", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={s.complianceScore ?? 0} min={0} max={10} onChange={(e) => updateSupplier(idx, "complianceScore", Number(e.target.value))} className="w-full rounded border px-2 py-1 text-xs text-right" />
                      </td>
                      <td className={`px-2 py-2 text-right font-bold ${isTop ? "text-emerald-700" : "text-slate-700"}`}>
                        {total}
                        {isTop && <span className="ml-1 text-xs">★</span>}
                      </td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => removeSupplier(idx)} disabled={suppliers.length === 1} className="text-red-400 hover:text-red-600 disabled:opacity-30 text-xs">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addSupplier} className="mt-3 rounded-lg border border-emerald-600 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
            + Add Supplier
          </button>
        </ProcSection>

        <ProcSection title="Recommendation">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Recommended Supplier</label>
              {namedSuppliers.length > 0 ? (
                <select
                  value={recommendedSupplierId}
                  onChange={(e) => setRecommendedSupplierId(e.target.value ? Number(e.target.value) : "")}
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                >
                  <option value="">— select —</option>
                  {namedSuppliers.map((s) => (
                    <option key={s.supplierId} value={s.supplierId!}>{s.supplierName}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={recommendedSupplierName}
                  onChange={(e) => setRecommendedSupplierName(e.target.value)}
                  placeholder="Supplier name"
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                />
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Recommendation Reason</label>
              <textarea
                value={recommendationReason}
                onChange={(e) => setRecommendationReason(e.target.value)}
                rows={3}
                className="w-full rounded-xl border px-3 py-2 text-sm"
              />
            </div>
          </div>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/evaluations" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
          <button
            type="button"
            onClick={() => mutate()}
            disabled={loading}
            className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {loading ? "Saving…" : "Save Evaluation"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
